import { BulletPool } from './BulletPool.js';
import { Skirmisher } from '../../entities/Skirmisher.js';
import { Charger } from '../../entities/Charger.js';
import { Bomber } from '../../entities/Bomber.js';
import { Warden } from '../../entities/Warden.js';
import { Caster } from '../../entities/Caster.js';
import { Trapper } from '../../entities/Trapper.js';
import { Splitter } from '../../entities/Splitter.js';
import { Leech } from '../../entities/Leech.js';
import { Swarmling } from '../../entities/Swarmling.js';
import { Orbiter } from '../../entities/Orbiter.js';
import { Anglerling } from '../../entities/Anglerling.js';
import { MirrorShade } from '../../entities/MirrorShade.js';
import { Muffler } from '../../entities/Muffler.js';
import { Sinkweight } from '../../entities/Sinkweight.js';
import { Tracer } from '../../entities/Tracer.js';
import { MimicMote } from '../../entities/MimicMote.js';
import { Tollkeeper } from '../../entities/Tollkeeper.js';
import { Twinbound } from '../../entities/Twinbound.js';
import { ARENA, WAVE_RAMP, SUICIDE, GRAZE, VIRTUAL_WIDTH, VIRTUAL_HEIGHT } from '../../../config/constants.js';

const EDGE_MARGIN = 20; // keeps spawned minions fully inside the arena rect, not straddling the wall

// Half-extents of the ring bodies spawn on: just past the edge of the view in
// each axis, **separately**.
//
// This used to be one circular radius, `hypot(halfW, halfH) + margin` — the
// view's half-DIAGONAL, ~246 — chosen so a spawn was offscreen no matter what
// direction it lay in. It is offscreen, and it is also nearly impossible in
// half of those directions, because the arena is 700x500 and 246 is further
// than the arena's usable half-height (230). A ring point straight above a
// centred player lands outside the arena and is rejected; a point to the left
// or right has 350 units to play with and is accepted. Measured arrival
// bearings, 8 sectors, before the change:
//
//   player centred ... 19 6 7 18 19 7 6 19   (horizontal 3x as likely)
//   player cornered ... 0 0 0 3 9 78 10 0    (78% from ONE sector)
//
// That is the "enemies arrive in corner clumps rather than surrounding you"
// symptom exactly: not a spawn-rate problem, a geometry problem. A rectangular
// ring matched to the viewport's own 384x216 aspect asks for ~192 units of
// clearance horizontally and ~108 vertically, both of which the arena has, so
// every bearing is actually available.
const SPAWN_RING_HALF_W = VIRTUAL_WIDTH / 2 + WAVE_RAMP.spawnRingMargin;
const SPAWN_RING_HALF_H = VIRTUAL_HEIGHT / 2 + WAVE_RAMP.spawnRingMargin;

/**
 * The world-space rect the camera shows with the player at `player`, at zoom 1.
 *
 * Mirrors Camera._clampToArena rather than reading the live camera: this
 * system has no camera reference (and is exercised headlessly in tests), and
 * wave stages have no boss, so the camera's spread-driven zoom-out never
 * engages and zoom is always 1 here. The follow lerp means the real view
 * trails this slightly, which WAVE_RAMP.spawnViewMargin covers.
 */
function viewRectFor(player) {
  const halfW = VIRTUAL_WIDTH / 2;
  const halfH = VIRTUAL_HEIGHT / 2;
  const minX = ARENA.x + halfW;
  const maxX = ARENA.x + ARENA.width - halfW;
  const minY = ARENA.y + halfH;
  const maxY = ARENA.y + ARENA.height - halfH;
  const cx = minX > maxX ? ARENA.x + ARENA.width / 2 : Math.min(maxX, Math.max(minX, player.x));
  const cy = minY > maxY ? ARENA.y + ARENA.height / 2 : Math.min(maxY, Math.max(minY, player.y));
  return { left: cx - halfW, right: cx + halfW, top: cy - halfH, bottom: cy + halfH };
}
// Default mix for any boss whose waveConfig doesn't specify minionTypes —
// pure Skirmishers, matching the original single-type behavior.
const DEFAULT_MINION_TYPES = [{ type: 'skirmisher', weight: 1 }];
// Every type _createMinion knows how to build — the pool randomMinionTypeSet
// draws from, so a run doesn't repeat the same curated mix every time.
// Deliberately does NOT include the Act II "deep" types (anglerling,
// mirrorShade, muffler, sinkweight — see ACT2-BIBLE.md §8). Those are the
// Undersong's own wave vocabulary, each echoing one Act II boss's texture, and
// they're mixed in explicitly by whichever boss they foreshadow
// (`waveConfig.deepMinionTypes`, folded in by Game._waveConfig). Leaving them
// out of the random pool is what keeps Act I's waves exactly as they were.
//
// The same holds for Act III's four (tracer, mimicMote, tollkeeper, twinbound
// — ACT3-BIBLE.md §8), declared per boss as `waveConfig.songMinionTypes` and
// merged the same way. Two fields rather than one shared list, so each act's
// wave vocabulary stays legible at the boss def and neither act's rolls can
// drift when the other's are edited.
const ALL_MINION_TYPES = [
  'skirmisher', 'charger', 'bomber', 'warden', 'caster',
  'trapper', 'splitter', 'leech', 'swarmling', 'orbiter',
];

/**
 * Rolls a fresh weighted minion mix — 3 to 5 types out of the full pool,
 * each with a random weight — meant to be called once per boss's wave
 * stage (see Game.js's _waveConfig) so the mix stays fixed across every
 * wave of that stage but reshuffles the next time a new stage starts.
 *
 * `random` is the run's wave stream on a seeded run (BULLET-HELL.md §D8) — the
 * per-stage mix is the single biggest thing a seed has to pin, since it decides
 * what the wave half of every stage is actually made of.
 */
export function randomMinionTypeSet(random = Math.random) {
  const pool = [...ALL_MINION_TYPES];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const count = 3 + Math.floor(random() * 3); // 3-5 types
  return pool.slice(0, count).map((type) => ({ type, weight: 1 + Math.floor(random() * 3) }));
}
/**
 * Introduction schedule for the ramp mode (BULLET-HEAVEN.md §H2). A stage's
 * rolled type set is filtered by stage progress so the ramp is qualitative as
 * well as numeric — skirmishers and chasers first, the vocabulary that needs
 * a real answer layered in later.
 *
 * Types not listed here are 'early': anything the player can deal with by
 * moving and shooting. 'mid' is the types that constrain WHERE you can go, and
 * 'late' is the types that constrain WHAT YOU CAN DO — a Trapper denies floor,
 * a Splitter turns one problem into three, a Muffler takes your weapons away.
 * Meeting all three in the opening ten seconds is the thing that made the old
 * first wave a coin flip.
 */
// Act III's four sort the same way: a Tracer or a Mimic Mote changes where you
// want to be, while a Tollkeeper (your motes) and a Twinbound (your kill order)
// change what you're allowed to do about it.
const MID_TYPES = new Set(['warden', 'caster', 'orbiter', 'leech', 'anglerling', 'sinkweight', 'tracer', 'mimicMote']);
const LATE_TYPES = new Set(['trapper', 'splitter', 'muffler', 'mirrorShade', 'tollkeeper', 'twinbound']);

// How many consecutive toned minions share a tone (BOSS-REWORK.md §B7).
// Three is the smallest block that reads as a block rather than as a run of
// coin flips, and small enough that a wave of eight Shades still alternates.
const SQUAD_TONE_SIZE = 3;

// Swarmling is a single wave-budget "slot" that expands into this many
// clustered bodies instead of one (see _spawnWave) — a texture contrast to
// every other type's one-slot-one-minion spawn.
// Raised from 4 with the crowd pass: Swarmling is the only true confetti type
// in the vocabulary — one-shot bodies whose whole job is to be deleted in
// bulk — and it is what a dense field needs more of, not more Trappers.
const SWARMLING_CLUSTER_SIZE = 6;
const SWARMLING_CLUSTER_SPREAD = 14; // px — jitter radius around the shared edge point

/**
 * Drives the wave-defense stage that precedes each boss: spawns
 * `waveConfig.minionsPerWave` minions (a weighted mix of types — see
 * `waveConfig.minionTypes`, rolled fresh per stage by Game.js's
 * `_waveConfig` via `randomMinionTypeSet` above) at the arena edges,
 * waits for all of them to die, then spawns the next wave — until
 * `waveConfig.waveCount` waves have cleared, at which point Game.js starts
 * the boss stage.
 *
 * Minions live directly in CombatSystem's shared enemy registry (so the
 * player's normal weapons can hit them) rather than a separate array here —
 * this system only tracks wave progression and owns the minions' own
 * ranged-shot bullet pool, mirroring how SpawningSystem owns the boss
 * bullet pool and player-vs-bullet collision.
 */
export class MinionWaveSystem {
  constructor(eventBus, combat, waveConfig, { rng = Math.random } = {}) {
    this.eventBus = eventBus;
    this.combat = combat;
    this.waveConfig = waveConfig;
    // The run's wave stream on a seeded run (BULLET-HELL.md §D8). One stream
    // shared by every stage's system, not one per stage: a per-stage generator
    // seeded from the same string would hand every stage of the run the same
    // sequence of type picks.
    this.rng = rng;
    this.pool = new BulletPool();
    this.waveIndex = 0;
    this._clearedEmitted = false;

    // Suicide bullets (BULLET-HELL.md §D6) — opt-in per stage, because the
    // mechanic is Act II+ vocabulary and Act I's ramp must not change. The
    // token bucket starts full so the stage's first kills read the same as
    // any later one.
    this.suicideBullets = Boolean(waveConfig.suicideBullets);
    this._burstTokens = SUICIDE.burstCap;

    // Ramp mode (BULLET-HEAVEN.md §H2) — opt-in per stage via
    // `waveConfig.rampMode`, which Game.js sets from a settings toggle so the
    // two shapes can be played back to back. Everything below this branch is
    // the original clear-then-next path, untouched.
    this.rampMode = Boolean(waveConfig.rampMode);
    if (this.rampMode) {
      this._initRamp();
    } else {
      this._spawnWave();
    }
  }

  // --- ramp mode (BULLET-HEAVEN.md §H2) ------------------------------------

  _initRamp() {
    // The two reinterpretations that let each boss's existing waveConfig
    // carry over rather than needing a parallel set of numbers.
    this.stageDuration = this.waveConfig.waveCount * WAVE_RAMP.secondsPerWaveUnit;
    // The crowd pass (WAVE_RAMP.crowdMultiplier): a boss's `minionsPerWave` is
    // a pressure budget, and peak concurrency is that budget spent on more,
    // weaker bodies. `_crowdSoftening` is the other half — the two must always
    // move together or this is just a difficulty increase wearing a density
    // costume. Opted out with `crowdScale: false` by any stage that authors its
    // concurrency directly rather than deriving it (the Undersong Tide does).
    this.crowdMultiplier = this.waveConfig.crowdScale === false ? 1 : WAVE_RAMP.crowdMultiplier;
    this.peakConcurrent = Math.round(this.waveConfig.minionsPerWave * this.crowdMultiplier);
    // Sized to keep the field full for the stage's whole duration, which the
    // old `waveCount * minionsPerWave` did not do. That figure was one field's
    // worth of bodies per 25s unit, and it bought volume parity with the
    // clear-then-next shape at the cost of the thing the ramp is FOR: measured
    // against a mid-power build, a wave stage ran out of budget partway and
    // spent 20-60% of its clock in an arena the spawner could no longer fill.
    // (Pre-existing — the crowd pass holds total effective HP constant, so it
    // neither caused this nor fixed it, it only made an empty arena obvious.)
    //
    // WAVE_RAMP.budgetTurnover is how many times over the field may be
    // replaced. Total volume is therefore no longer at parity with the old
    // shape — but instantaneous pressure is, which is the parity that governs
    // whether a stage is hard. A stage that ends on its clock with the field
    // still full delivers more total bodies than one that ended when you had
    // killed a fixed number of them; that is what ending on a clock MEANS.
    //
    // Still finite: a build that outruns even this genuinely finished the
    // stage, and `_updateRamp`'s budget-spent branch lets it leave early
    // rather than stand in an empty arena waiting out the timer.
    //
    // Overridable because that derivation is a WAVE STAGE's, not the ramp's.
    // The Undersong Tide (§H7) must keep the screen full for twenty minutes
    // against a build that is deleting bodies faster every minute — there, the
    // clock has to be the only thing that ends the stage, so it passes a budget
    // large enough that this can never bind. See data/tide.js.
    this.budgetSlots = this.waveConfig.budgetSlots
      ?? Math.round(this.waveConfig.waveCount * this.peakConcurrent * WAVE_RAMP.budgetTurnover);
    // Refill ceiling, overridable for the same reason the budget is. The
    // default keeps a cleared wave-stage field from re-filling in one instant
    // (which reads as enemies teleporting in), and at a wave stage's peak of
    // 8-13 that ceiling is never the binding constraint.
    //
    // At the Tide's peak of 40 it is: three bodies per 0.45s is under seven
    // slots a second, which a snowballed build outruns outright — the arena
    // then sits EMPTY through the mode's back half, which is the exact
    // failure BULLET-HEAVEN.md §4 describes (a game that snowballs into an
    // empty screen has spent its drug). Measured by tideStage.test.js, which
    // counts empty ticks for precisely this reason.
    //
    // The floor of 3 was sized for the pre-crowd peaks of 8-13, where it never
    // bound. Post-crowd peaks are 22-39 and it binds hard — a cleared field
    // would take five seconds to come back, which is the lull again. Scaled off
    // the peak so a full refill always takes about the same ~1.5-2s regardless
    // of how dense the stage is.
    this.maxSpawnBurst = this.waveConfig.maxSpawnBurst
      ?? Math.max(WAVE_RAMP.maxSpawnBurst, Math.ceil(this.peakConcurrent / 6));
    this.elapsed = 0;
    this.slotsSpawned = 0;
    this._spawnCooldown = 0;
    // Reported to the HUD in place of a wave counter; also what `_waveScale`
    // reads so the within-stage stat ramp still exists in this mode.
    this.waveIndex = 0;
    this.eventBus.emit('wave:started', { index: 1, total: this.waveConfig.waveCount, ramp: true });
  }

  /** 0..1 through the stage clock. */
  get rampProgress() {
    return this.stageDuration > 0 ? Math.min(1, this.elapsed / this.stageDuration) : 1;
  }

  /** How many bodies the stage wants on the field right now. */
  get concurrentTarget() {
    const u = this.rampProgress;
    const fraction = WAVE_RAMP.concurrentStart
      + (1 - WAVE_RAMP.concurrentStart) * Math.pow(u, WAVE_RAMP.concurrentExponent);
    // At least one, so a stage is never briefly empty mid-ramp — an empty
    // field is the lull this whole mode exists to remove.
    return Math.max(1, Math.round(this.peakConcurrent * fraction));
  }

  /**
   * Types allowed on the field at the current point in the ramp. Falls back to
   * the unfiltered set if the schedule would leave nothing to spawn — a stage
   * whose rolled mix happens to be entirely 'late' types must still start.
   */
  _allowedTypes() {
    const all = this.waveConfig.minionTypes ?? DEFAULT_MINION_TYPES;
    const u = this.rampProgress;
    if (u >= WAVE_RAMP.lateTypesAt) return all;
    const blocked = u >= WAVE_RAMP.midTypesAt ? LATE_TYPES : new Set([...MID_TYPES, ...LATE_TYPES]);
    const allowed = all.filter((entry) => !blocked.has(entry.type));
    return allowed.length > 0 ? allowed : all;
  }

  _updateRamp(dt) {
    this.elapsed += dt;
    // Drives the existing per-wave stat scale off stage progress instead of a
    // wave counter, so minions still get tougher across a stage exactly as
    // much as they used to by its end.
    this.waveIndex = Math.min(
      this.waveConfig.waveCount - 1,
      Math.floor(this.rampProgress * this.waveConfig.waveCount),
    );

    this._spawnCooldown -= dt;
    if (this._spawnCooldown <= 0 && this.slotsSpawned < this.budgetSlots) {
      this._spawnCooldown = WAVE_RAMP.spawnInterval;
      const deficit = this.concurrentTarget - this.combat.minions.length;
      const count = Math.min(deficit, this.maxSpawnBurst, this.budgetSlots - this.slotsSpawned);
      for (let i = 0; i < count; i++) this._spawnOneSlot();
    }

    if (this._clearedEmitted) return;
    // Two ways a ramp stage ends. The clock is the primary one — that is the
    // whole point of the mode, and it is what makes the pressure curve a
    // promise rather than a race. But a player fast enough to burn the entire
    // budget early has genuinely finished the stage, and holding them in an
    // empty arena waiting out a timer would be the lull all over again.
    //
    // `combat.minions.length === 0` is also the pause contract the Echo Duel
    // relies on (ARCHIVE.md §5): the ghost lives in the minion list, so it
    // holds the stage open exactly as it did before. The clock branch honours
    // it explicitly for the same reason — a duel must not be cut off by a
    // timer expiring mid-fight.
    const budgetSpent = this.slotsSpawned >= this.budgetSlots;
    const fieldClear = this.combat.minions.length === 0;
    const timeUp = this.elapsed >= this.stageDuration;
    if ((timeUp && !this._hasDuelInProgress()) || (budgetSpent && fieldClear)) {
      this._clearedEmitted = true;
      this.eventBus.emit('wave:cleared', { waves: this.waveConfig.waveCount, ramp: true });
    }
  }

  /**
   * True while a boss-tier body (the Echo Duel ghost — see Game._updateEchoRift)
   * is standing in the minion list. Ordinary minions never set isBossTier, so
   * this is precisely "a duel is running", not "the field is busy".
   */
  _hasDuelInProgress() {
    return this.combat.minions.some((m) => m.isBossTier);
  }

  /** One budget slot: a single body, or a Swarmling cluster, spawned offscreen. */
  _spawnOneSlot() {
    const config = this._scaledWaveConfig();
    const { x, y } = this._spawnPosition();
    const type = this._pickType(this._allowedTypes());
    const spawned = [];
    if (type === 'swarmling') {
      for (let s = 0; s < SWARMLING_CLUSTER_SIZE; s++) {
        spawned.push(this._createMinion(
          type,
          x + (Math.random() * 2 - 1) * SWARMLING_CLUSTER_SPREAD,
          y + (Math.random() * 2 - 1) * SWARMLING_CLUSTER_SPREAD,
          config,
        ));
      }
    } else {
      spawned.push(this._createMinion(type, x, y, config));
    }
    // Wardens link against whoever is already on the field, not just their own
    // spawn batch: in a continuous ramp a batch is one or two bodies, and a
    // Warden that can only shield its own batch would usually shield nothing.
    this._linkWardens([...spawned, ...this.combat.minions]);
    this._pairTwins(spawned);
    for (const minion of spawned) this.combat.addMinion(minion);
    this.slotsSpawned++;
  }

  // --- shared -------------------------------------------------------------

  get isStageCleared() {
    // Ramp mode ends on its own clock and nothing else — deliberately WITHOUT
    // the "field is empty" term the clear-then-next rule needs.
    //
    // That term looks harmless and is not: a ramp stage expires with its peak
    // concurrency still alive by design (that IS the ramp), so requiring an
    // empty field would hold the stage open until the player mopped up, which
    // is the clear-then-next lull reintroduced at the worst possible moment.
    // In the Undersong Tide (§H7), where the field holds forty bodies at the
    // twenty-minute mark, the reaper would simply never arrive.
    //
    // Game.js's stage transitions already sweep leftover minions, and flush
    // their banked mote XP first (see ProgressionSystem.flushBankedXp), so
    // ending on the clock costs the player nothing they had earned.
    if (this.rampMode) return this._clearedEmitted;
    return this.waveIndex >= this.waveConfig.waveCount && this.combat.minions.length === 0;
  }

  update(dt, player) {
    // Held so the spawners can place bodies relative to the player's view
    // without threading it through every call site. Undefined on the very
    // first clear-then-next wave, which the constructor spawns before any
    // update has run — that one falls back to an arena-edge spawn.
    this._player = player;
    this.pool.update(dt);
    this._checkPlayerBulletCollisions(player);

    // The Muffler (Act II) mutes the player from inside its bubble by writing
    // this flag every frame it applies. Clearing it here — before any minion
    // updates — makes this system its single owner for the whole wave stage,
    // so the mute can never stick past a Muffler's death or the player
    // stepping out. The boss-stage owner is the Hushfall pattern, and the two
    // never overlap (minions are cleared before a boss stage starts).
    player.muted = false;

    // allMinions (4th arg) is only read by Leech's heal-aura pulse — every
    // other type's update() signature just ignores the extra param.
    // sourceLabel stamps each shot with the type that fired it (see
    // BulletPool) so the death recap can name it; minions never touch it.
    for (const minion of this.combat.minions) {
      this.pool.sourceLabel = minion.type ?? null;
      minion.update(dt, player, this.pool, this.combat.minions);
    }
    this.pool.sourceLabel = null;

    // Refilled before the sweep so a wipe that happens on this very frame can
    // still spend the bucket it arrived with.
    this._refillBurstTokens(dt);
    for (const minion of [...this.combat.minions]) {
      if (minion.isDead) {
        // Fired from here, off the dead body still in the registry, rather than
        // from a `minion:died` listener: this system is per wave stage and
        // never unsubscribes, so a listener would leak a stage's worth of
        // bursts into the next one. The sweep is also the single place every
        // death funnels through (CombatSystem leaves removal to us — see its
        // comment on the refrain carriers), so nothing can die unseen here.
        this._fireSuicideBurst(minion, player);
        this.combat.removeMinion(minion);
      }
    }

    if (this.rampMode) {
      this._updateRamp(dt);
      return;
    }

    if (this.combat.minions.length === 0) {
      if (this.waveIndex < this.waveConfig.waveCount) {
        this._spawnWave();
      } else if (!this._clearedEmitted) {
        // The whole stage just cleared (isStageCleared flips true this same
        // tick — Game.js transitions right after this update returns).
        // Wave-to-wave handoffs are instant and announced by the next
        // wave:started; this marks only the stage-level clear.
        this._clearedEmitted = true;
        this.eventBus.emit('wave:cleared', { waves: this.waveConfig.waveCount });
      }
    }
  }

  _spawnWave() {
    const config = this._scaledWaveConfig();
    const { minionsPerWave, minionTypes = DEFAULT_MINION_TYPES } = config;
    const spawned = [];
    for (let i = 0; i < minionsPerWave; i++) {
      const { x, y } = this._spawnPosition();
      const type = this._pickType(minionTypes);
      if (type === 'swarmling') {
        // One wave-budget slot expands into a clustered burst of easy
        // kills instead of a single medium-difficulty body.
        for (let s = 0; s < SWARMLING_CLUSTER_SIZE; s++) {
          const jx = x + (Math.random() * 2 - 1) * SWARMLING_CLUSTER_SPREAD;
          const jy = y + (Math.random() * 2 - 1) * SWARMLING_CLUSTER_SPREAD;
          spawned.push(this._createMinion(type, jx, jy, config));
        }
      } else {
        spawned.push(this._createMinion(type, x, y, config));
      }
    }
    this._linkWardens(spawned);
    this._pairTwins(spawned);
    for (const minion of spawned) this.combat.addMinion(minion);
    this.waveIndex++;
    // 1-based index (incremented just above) — matches the WAVE N/TOTAL
    // label the HUD already shows.
    this.eventBus.emit('wave:started', { index: this.waveIndex, total: this.waveConfig.waveCount });
  }

  // Waves within a stage ramp together — wave 1 (waveIndex 0) is exactly the
  // configured baseline so nothing here changes a single-wave stage or the
  // numbers documented in data/bosses.js; each wave after that gets tougher,
  // building to the hardest wave right before the boss fight. Speed/fire-rate
  // ramp gentler than HP/damage so movement stays readable at the end of a
  // long stage.
  //
  // `waveConfig.scaleCaps` ceilings each term. These rates were authored for a
  // 4-wave stage — 3 steps of headroom — and in ramp mode `waveIndex` is
  // derived from stage progress across `waveCount` units, so a long stage
  // extrapolates them far past their design range. The Undersong Tide runs 48
  // units: uncapped, that is 9.5x HP and 8x contact damage on TRASH by minute
  // twenty, which is a per-body lethality no amount of player power makes
  // standable — and standing in the crowd is the entire mode. Capping is what
  // lets density, rather than per-body numbers, carry the late game.
  _waveScale() {
    const step = this.waveIndex;
    const caps = this.waveConfig.scaleCaps ?? {};
    const capped = (value, cap) => (cap == null ? value : Math.min(value, cap));
    return {
      hp: capped(1 + step * 0.18, caps.hp),
      damage: capped(1 + step * 0.15, caps.damage),
      speed: capped(1 + step * 0.06, caps.speed),
      fireRate: Math.max(caps.fireRate ?? 0.6, 1 - step * 0.08),
    };
  }

  /**
   * The other half of the crowd pass (WAVE_RAMP.crowdMultiplier), and the
   * reason raising concurrency is not a difficulty increase. See that constant
   * for why HP divides by the full multiplier and damage by its square root.
   */
  _crowdSoftening() {
    if (!this.rampMode || (this.crowdMultiplier ?? 1) <= 1) return { hp: 1, damage: 1 };
    // The REALIZED ratio, not the nominal constant: `peakConcurrent` is
    // rounded to whole bodies, so at a peak of 8 the actual multiplier is
    // 22/8 = 2.75, not 2.8. Softening by the nominal figure would leave the
    // stage's total HP off by a couple of percent in a direction that varies
    // per boss — small, but it means the parity this is supposed to guarantee
    // would not actually hold anywhere.
    const c = this.peakConcurrent / this.waveConfig.minionsPerWave;
    return { hp: 1 / c, damage: 1 / Math.sqrt(c) };
  }

  _scaledWaveConfig() {
    const c = this.waveConfig;
    const s = this._waveScale();
    const crowd = this._crowdSoftening();
    return {
      ...c,
      minionHp: c.minionHp * s.hp * crowd.hp,
      minionMoveSpeed: c.minionMoveSpeed * s.speed,
      minionContactDamage: c.minionContactDamage * s.damage * crowd.damage,
      minionShotDamage: c.minionShotDamage * s.damage * crowd.damage,
      minionShotSpeed: c.minionShotSpeed * s.speed,
      minionFireInterval: c.minionFireInterval * s.fireRate,
    };
  }

  /**
   * Twinbounds are bound two at a time, in spawn order (see Twinbound.js). An
   * odd one out is left unpaired and degrades to a plain chaser, which is the
   * right failure: a creature whose partner is nothing has nothing to mend.
   */
  _pairTwins(spawned) {
    const twins = spawned.filter((m) => m.type === 'twinbound');
    for (let i = 0; i + 1 < twins.length; i += 2) {
      twins[i].partner = twins[i + 1];
      twins[i + 1].partner = twins[i];
      // Mirrored placement, so the pair reads as one thing in two places from
      // the moment it arrives — the Manifold's image, at minion scale.
      const cx = ARENA.x + ARENA.width / 2;
      const cy = ARENA.y + ARENA.height / 2;
      twins[i + 1].x = cx * 2 - twins[i].x;
      twins[i + 1].y = cy * 2 - twins[i].y;
      twins[i + 1]._clampToArena();
    }
  }

  /** Each Warden in the wave shields behind a random non-Warden sibling from the same wave (see Warden.js). */
  _linkWardens(spawned) {
    const shieldable = spawned.filter((m) => m.type !== 'warden');
    if (shieldable.length === 0) return;
    for (const minion of spawned) {
      if (minion.type === 'warden') {
        minion.linkedMinion = shieldable[Math.floor(Math.random() * shieldable.length)];
      }
    }
  }

  /**
   * Weighted pick from waveConfig's minionTypes (e.g. [{type:'charger', weight:2}, ...]).
   *
   * Draws from the run's wave stream on a seeded run (BULLET-HELL.md §D8), so a
   * fixed-wave stage spawns the same bodies in the same order. Positions, edge
   * choice and every other spawn-time jitter below deliberately stay on
   * `Math.random`: they are texture, not content, and in ramp mode their call
   * counts depend on how fast the player is clearing — see core/Rng.js on why a
   * seed pins the deck rather than the frames.
   */
  _pickType(minionTypes) {
    const totalWeight = minionTypes.reduce((sum, t) => sum + t.weight, 0);
    let roll = this.rng() * totalWeight;
    for (const t of minionTypes) {
      roll -= t.weight;
      if (roll <= 0) return t.type;
    }
    return minionTypes[minionTypes.length - 1].type;
  }

  /**
   * The tone for the next toned minion (§B7): the same one for a whole squad
   * of `SQUAD_TONE_SIZE`, then the other. Counted across the wave rather than
   * reset per spawn call, so a wave reads as alternating blocks; the lead tone
   * flips each wave so the opening answer is never the same twice running.
   */
  _nextSquadTone() {
    this._squadToneCount = (this._squadToneCount ?? 0) + 1;
    const block = Math.floor((this._squadToneCount - 1) / SQUAD_TONE_SIZE);
    const flipped = (block + this.waveIndex) % 2 === 0;
    return flipped ? 'warm' : 'cold';
  }

  _createMinion(type, x, y, config) {
    const { minionHp, minionMoveSpeed, minionContactDamage, minionShotDamage, minionShotSpeed, minionFireInterval } = config;
    switch (type) {
      case 'charger':
        // Slower base approach than a Skirmisher (its charge burst is what
        // makes it dangerous), noticeably harder contact hit.
        return new Charger(this.eventBus, {
          x, y, hp: minionHp, moveSpeed: minionMoveSpeed * 0.8, contactDamage: minionContactDamage * 1.8,
        });
      case 'bomber':
        return new Bomber(this.eventBus, {
          x, y, hp: minionHp, moveSpeed: minionMoveSpeed * 0.7, contactDamage: minionContactDamage,
          explodeDamage: minionContactDamage * 2.5, explodeRadius: 30,
        });
      case 'warden':
        // Tougher and slower than a Skirmisher — the shield is its defense,
        // not its stats. linkedMinion is assigned after the whole wave has
        // spawned (see _linkWardens), not here.
        return new Warden(this.eventBus, {
          x, y, hp: minionHp * 1.4, moveSpeed: minionMoveSpeed * 0.85, contactDamage: minionContactDamage,
        });
      case 'caster':
        return new Caster(this.eventBus, {
          x, y, hp: minionHp * 0.8, moveSpeed: minionMoveSpeed, contactDamage: minionContactDamage * 0.6,
          shotDamage: minionShotDamage * 1.6, shotSpeed: minionShotSpeed * 0.8, fireInterval: minionFireInterval * 1.3,
        });
      case 'trapper':
        return new Trapper(this.eventBus, {
          x, y, hp: minionHp, moveSpeed: minionMoveSpeed * 0.75, contactDamage: minionContactDamage * 0.5,
          mineDamage: minionContactDamage * 2, mineRadius: 26,
        });
      case 'splitter':
        return new Splitter(this.eventBus, {
          x, y, hp: minionHp * 0.9, moveSpeed: minionMoveSpeed * 0.9, contactDamage: minionContactDamage,
          childHp: minionHp * 0.35, childMoveSpeed: minionMoveSpeed * 1.1, childContactDamage: minionContactDamage * 0.6,
          childShotDamage: minionShotDamage * 0.6, childShotSpeed: minionShotSpeed, childFireInterval: minionFireInterval * 1.4,
        });
      case 'leech':
        return new Leech(this.eventBus, {
          x, y, hp: minionHp * 0.5, moveSpeed: minionMoveSpeed * 0.6, contactDamage: minionContactDamage * 0.5,
        });
      case 'swarmling':
        // hp: 1 rather than a waveConfig-derived value — see Swarmling.js,
        // it's always meant to be a guaranteed one-shot regardless of stage.
        return new Swarmling(this.eventBus, {
          x, y, hp: 1, moveSpeed: minionMoveSpeed * 1.6, contactDamage: minionContactDamage * 0.4,
        });
      case 'orbiter':
        return new Orbiter(this.eventBus, {
          x, y, hp: minionHp * 0.7, moveSpeed: minionMoveSpeed * 1.1, contactDamage: minionContactDamage * 0.5,
          shotDamage: minionShotDamage * 0.8, shotSpeed: minionShotSpeed, fireInterval: minionFireInterval * 0.9,
        });
      // --- Act II: the deep (ACT2-BIBLE.md §8) ---
      case 'anglerling':
        // Fast and fragile — the threat is not seeing it coming, not its HP.
        return new Anglerling(this.eventBus, {
          x, y, hp: minionHp * 0.55, moveSpeed: minionMoveSpeed * 1.25, contactDamage: minionContactDamage * 0.9,
        });
      case 'mirrorShade':
        // Tone arrives in SQUADS, not per body (BOSS-REWORK.md §B7). A coin
        // flip per Shade is noise, and the correct play against noise is to
        // ignore it; Ikaruga's polarity is a reading problem precisely because
        // colour arrives in legible blocks you route through. A wave still
        // carries both tones — the squads alternate — so no single attunement
        // clears it and flipping is still the whole lesson.
        return new MirrorShade(this.eventBus, {
          x, y, hp: minionHp * 0.75, moveSpeed: minionMoveSpeed, contactDamage: minionContactDamage,
          tone: this._nextSquadTone(),
        });
      case 'muffler':
        // Slow and low-damage: it isn't the thing that kills you, it's the
        // thing that stops you answering whatever is.
        return new Muffler(this.eventBus, {
          x, y, hp: minionHp * 0.9, moveSpeed: minionMoveSpeed * 0.7, contactDamage: minionContactDamage * 0.5,
        });
      case 'sinkweight':
        // Tanky and slow — it's terrain that walks, not a chaser.
        return new Sinkweight(this.eventBus, {
          x, y, hp: minionHp * 1.6, moveSpeed: minionMoveSpeed * 0.5, contactDamage: minionContactDamage * 1.2,
        });
      // --- Act III: the new song (ACT3-BIBLE.md §8) ---
      case 'tracer':
        // Quick and fragile: the threat is *where* it goes, not what it can
        // take. It never approaches you at all — only where you have been.
        return new Tracer(this.eventBus, {
          x, y, hp: minionHp * 0.6, moveSpeed: minionMoveSpeed * 1.2, contactDamage: minionContactDamage * 1.1,
        });
      case 'mimicMote':
        // Small and weak, and carrying your own weapon's colour — the tell is
        // the whole creature, so the numbers stay low.
        return new MimicMote(this.eventBus, {
          x, y, hp: minionHp * 0.5, moveSpeed: minionMoveSpeed, contactDamage: minionContactDamage * 0.5,
          shotDamage: minionShotDamage * 0.8, shotSpeed: minionShotSpeed, fireInterval: minionFireInterval,
          // Whatever the player is actually holding. Falls back to the act's
          // green in the headless/no-combat case.
          mirrorColor: this.combat?.weapons?.[0]?.config?.color ?? '#7fe0a0',
        });
      case 'tollkeeper':
        // Tanky and harmless: it can't kill you, so its HP is the entire
        // decision — spend the time or lose the motes.
        return new Tollkeeper(this.eventBus, {
          x, y, hp: minionHp * 1.8, moveSpeed: minionMoveSpeed * 0.45, contactDamage: minionContactDamage * 0.4,
        });
      case 'twinbound':
        // Ordinary stats; the pair bond is the fight. Paired after the wave has
        // spawned (see _pairTwins), exactly as Wardens are linked.
        return new Twinbound(this.eventBus, {
          x, y, hp: minionHp * 0.85, moveSpeed: minionMoveSpeed, contactDamage: minionContactDamage,
        });
      default:
        return new Skirmisher(this.eventBus, {
          x, y, hp: minionHp, moveSpeed: minionMoveSpeed, contactDamage: minionContactDamage,
          shotDamage: minionShotDamage, shotSpeed: minionShotSpeed, fireInterval: minionFireInterval,
        });
    }
  }

  /**
   * Where a new body enters the fight: a point on a ring around the player,
   * just outside the view, rejection-sampled so it lands inside the arena and
   * out of sight. Falls back to an arena-edge spawn when there is no player
   * yet, or when the player is cornered tightly enough that the ring has no
   * valid arc left (see WAVE_RAMP.spawnRingAttempts).
   *
   * The old arena-edge placement is why concurrency never translated into
   * density: a body spawned at the far wall spends several seconds walking
   * through fog while the spawner counts it as already on the field.
   */
  _spawnPosition() {
    const player = this._player;
    if (!player) return this._randomEdgePosition();

    const view = viewRectFor(player);
    const m = WAVE_RAMP.spawnViewMargin;
    const minX = ARENA.x + EDGE_MARGIN;
    const maxX = ARENA.x + ARENA.width - EDGE_MARGIN;
    const minY = ARENA.y + EDGE_MARGIN;
    const maxY = ARENA.y + ARENA.height - EDGE_MARGIN;

    const offscreen = (x, y) => x < view.left - m || x > view.right + m
      || y < view.top - m || y > view.bottom + m;

    // A point on the perimeter of the view-shaped rect, pushed out by jitter.
    // Sampling the perimeter by *length* rather than by angle is the other
    // half of the even-arrival fix: picking a uniform angle and projecting it
    // onto a 384x216 rect piles arrivals onto the short edges, because equal
    // slices of angle cover very unequal amounts of the top and bottom runs.
    // Perimeter-length sampling gives every point on the ring the same
    // chance, which is what "surrounded" means.
    for (let attempt = 0; attempt < WAVE_RAMP.spawnRingAttempts; attempt++) {
      const depth = Math.random() * WAVE_RAMP.spawnRingJitter;
      const halfW = SPAWN_RING_HALF_W + depth;
      const halfH = SPAWN_RING_HALF_H + depth;
      let x;
      let y;
      const t = Math.random() * (halfW + halfH) * 4;
      if (t < halfW * 2) { // top edge
        x = player.x - halfW + t;
        y = player.y - halfH;
      } else if (t < halfW * 2 + halfH * 2) { // right edge
        x = player.x + halfW;
        y = player.y - halfH + (t - halfW * 2);
      } else if (t < halfW * 4 + halfH * 2) { // bottom edge
        x = player.x + halfW - (t - halfW * 2 - halfH * 2);
        y = player.y + halfH;
      } else { // left edge
        x = player.x - halfW;
        y = player.y + halfH - (t - halfW * 4 - halfH * 2);
      }
      if (x < minX || x > maxX || y < minY || y > maxY) continue;
      if (offscreen(x, y)) return { x, y };
    }

    // The ring had no valid arc — the player is cornered hard enough that most
    // of it is outside the arena. Fall back to the old arena-edge placement,
    // but still rejection-sample it for visibility: a corner is exactly where
    // an unfiltered edge spawn lands in frame, so taking the first one would
    // pop a body into view precisely when the ring was most needed. The view
    // covers ~24% of the arena and never touches the whole perimeter, so an
    // offscreen edge point effectively always exists.
    for (let attempt = 0; attempt < WAVE_RAMP.spawnRingAttempts; attempt++) {
      const p = this._randomEdgePosition();
      if (offscreen(p.x, p.y)) return p;
    }
    return this._randomEdgePosition();
  }

  _randomEdgePosition() {
    const side = Math.floor(Math.random() * 4);
    const spanX = ARENA.width - EDGE_MARGIN * 2;
    const spanY = ARENA.height - EDGE_MARGIN * 2;
    switch (side) {
      case 0: // top
        return { x: ARENA.x + EDGE_MARGIN + Math.random() * spanX, y: ARENA.y + EDGE_MARGIN };
      case 1: // right
        return { x: ARENA.x + ARENA.width - EDGE_MARGIN, y: ARENA.y + EDGE_MARGIN + Math.random() * spanY };
      case 2: // bottom
        return { x: ARENA.x + EDGE_MARGIN + Math.random() * spanX, y: ARENA.y + ARENA.height - EDGE_MARGIN };
      default: // left
        return { x: ARENA.x + EDGE_MARGIN, y: ARENA.y + EDGE_MARGIN + Math.random() * spanY };
    }
  }

  // --- suicide bullets (BULLET-HELL.md §D6) ---------------------------------

  /** Tops the burst bucket back up — see SUICIDE.burstsPerSecond for why one exists. */
  _refillBurstTokens(dt) {
    if (!this.suicideBullets) return;
    this._burstTokens = Math.min(SUICIDE.burstCap, this._burstTokens + SUICIDE.burstsPerSecond * dt);
  }

  /**
   * A dying minion's parting fan, aimed at the player.
   *
   * Every gate here is a fairness gate, not a difficulty dial: the type
   * exemptions keep a death from saying two things at once, the range check
   * keeps a burst from arriving with no tell, and the token bucket keeps a
   * field wipe from answering the player's best moment with a wall. What is
   * left is the mechanic — kill faster, dodge more.
   */
  _fireSuicideBurst(minion, player) {
    if (!this.suicideBullets || !player) return;
    // The Echo Duel ghost is a boss standing in the minion list (see
    // _hasDuelInProgress) and dies to end its own fight; a parting fan from a
    // corpse mid-victory-beat is not what that moment is.
    if (minion.isBossTier) return;
    if (SUICIDE.exemptTypes.has(minion.type)) return;
    if (this._burstTokens < 1) return;

    const dx = player.x - minion.x;
    const dy = player.y - minion.y;
    const dist = Math.hypot(dx, dy);
    if (dist > SUICIDE.maxRange) return;
    // Rolled last, so a declined roll doesn't spend a token or hide a bug in
    // any of the gates above behind randomness.
    if (Math.random() >= SUICIDE.chance) return;
    this._burstTokens -= 1;

    const config = this._scaledWaveConfig();
    const span = SUICIDE.maxBullets - SUICIDE.minBullets + 1;
    const count = SUICIDE.minBullets + Math.floor(Math.random() * span);
    const aim = Math.atan2(dy, dx);
    const speed = config.minionShotSpeed * SUICIDE.speedScale;
    const damage = config.minionShotDamage * SUICIDE.damageScale;
    // Attributed to the mechanic rather than the type — see SUICIDE.source.
    this.pool.sourceLabel = SUICIDE.source;
    for (let i = 0; i < count; i++) {
      // Evenly spaced across the cone rather than randomly sprayed: a fan has
      // a gap you can read and step into, a spray only has luck.
      const t = count === 1 ? 0.5 : i / (count - 1);
      const angle = aim + (t - 0.5) * SUICIDE.spread;
      this.pool.fire({
        x: minion.x,
        y: minion.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: SUICIDE.radius,
        damage,
        color: SUICIDE.color,
        lifetime: SUICIDE.lifetime,
      });
    }
    this.pool.sourceLabel = null;
    this.eventBus.emit('minion:death-burst', { x: minion.x, y: minion.y, count, type: minion.type ?? null });
  }

  _checkPlayerBulletCollisions(player) {
    this.pool.forEachActive((bullet) => {
      const dist = Math.hypot(bullet.x - player.x, bullet.y - player.y);
      const hitDist = bullet.radius + player.radius;
      // Graze (BULLET-HELL.md §D5), by exactly the rule
      // SpawningSystem._checkPlayerCollisions uses — see that comment for why a
      // dash through the hull grazes and a Phase Step dodge doesn't.
      //
      // Minion fire was left out of this when the chain shipped, which made
      // grazing a boss-room-only skill: a wave stage is most of a run's clock,
      // and threading a Caster's line paid nothing while threading an identical
      // boss bullet paid charge, Glimmer and magnet radius. §D6's death bursts
      // make that gap worse — the wave half now asks for dodging, and a mechanic
      // that rewards dodging has to be live where the dodging happens.
      const inBand = dist < hitDist + GRAZE.band;
      if (!bullet.grazed && !bullet.grazePending && inBand
        && !player.isHitInvulnerable
        && (dist >= hitDist || player.isInvincible)) {
        bullet.grazePending = true;
      } else if (bullet.grazePending && !inBand) {
        bullet.grazePending = false;
        bullet.grazed = true;
        this.eventBus.emit('bullet:grazed', { x: bullet.x, y: bullet.y });
      }
      if (dist < hitDist) {
        // Settles the deferred graze exactly as SpawningSystem does: a bullet
        // that reaches the hull pays nothing unless dash i-frames are up.
        if (bullet.grazePending) {
          bullet.grazePending = false;
          if (player.isInvincible) {
            bullet.grazed = true;
            this.eventBus.emit('bullet:grazed', { x: bullet.x, y: bullet.y });
          }
        }
        // Same rule as SpawningSystem._checkPlayerCollisions: the post-hit
        // mercy window lets a bullet wash through instead of consuming it,
        // while dash i-frames still absorb.
        if (player.isHitInvulnerable && !player.isInvincible) return;
        player.takeDamage(bullet.damage, bullet.source);
        bullet.active = false;
      }
    });
  }

  forEachBullet(fn) {
    this.pool.forEachActive(fn);
  }
}
